import React, { memo, useEffect, useMemo } from 'react';
import { RouteFC } from 'util/routeTypes';
import { useMerchant } from 'hooks/context/merchant';
import { useCloverPaymentIframeContext } from 'hooks/context/cloverPaymentIframe';
import { useWindowSize } from 'hooks/breakpoints';
import { Mobile } from './Mobile/Mobile';
import { Tablet } from './Tablet/Tablet';
import { Matryoshka } from '@clover-util/matryoshka';
import { useInvoiceSummary } from './context/InvoiceSummary';
import { CheckoutValidityProvider } from './context/CheckoutValidity';
import { OrderDetailsProvider } from './context/OrderDetails';
import { OverlaySpinner, Spinner } from 'components/Spinner';
import { PageLayout } from 'components/PageLayout';
import { ScrollToTop } from 'components/ScrollToTop';
import { useSubmitOrder, SubmitOrderProvider } from './context/SubmitOrder/SubmitOrder';
import { ApplePayProvider } from 'components/ApplePay/ApplePay';
import { OrderResponseSummaryProvider } from './context/OrderResponseSummary';
import { isPaymentProcessorIpg } from 'util/IpgPay';
import { useCustomer } from 'hooks/context/customer/customer';
import { useOptimizelyTrack } from '@clover-util/optimizely';
import { useDeliveryValidation } from 'hooks/useDeliveryValidation';
import { useLocation } from '@reach/router';
import { OrderHistoryProvider } from 'hooks/context/OrderHistory';

const Blocker: React.FC = () => {
  const location = useLocation();
  const { isInvoiceSummaryLoading } = useInvoiceSummary();
  const { isOrderProcessing } = useSubmitOrder();
  const deliveryValidation = useDeliveryValidation();
  const addressModalIsOpen = useMemo(() => location.pathname.split('/').includes('address'), [location.pathname]);

  if (isInvoiceSummaryLoading || isOrderProcessing || (deliveryValidation.isLoading && !addressModalIsOpen)) {
    return <OverlaySpinner />;
  }

  return null;
};

const CHECKOUT_PROVIDERS = [
  CheckoutValidityProvider,
  OrderDetailsProvider,
  OrderResponseSummaryProvider,
  ApplePayProvider,
  SubmitOrderProvider,
  ScrollToTop as React.ComponentType<{children?: React.ReactNode;}>,
  OrderHistoryProvider,
];

export const Checkout: RouteFC<{ children: never }> = memo(() => {
  const { isDesktop } = useWindowSize();
  const { merchant, isLoading } = useMerchant();
  const { isScriptLoading } = useCloverPaymentIframeContext();
  const { isLoggedIn, isGetCustomerLoading } = useCustomer();
  const track = useOptimizelyTrack();

  const readyForPayment = useMemo(() => !isScriptLoading || isPaymentProcessorIpg(merchant?.paymentMethods), [isScriptLoading, merchant]);
  const isCustomerLoading = useMemo(() => isLoggedIn && isGetCustomerLoading, [isLoggedIn, isGetCustomerLoading]);
  const isDataLoading = useMemo(() => !readyForPayment || !merchant || isLoading || isCustomerLoading, [readyForPayment, merchant, isLoading, isCustomerLoading]);

  useEffect(() => {
    if (isDataLoading) return;
    track('checkout');

    return () => track('unmount');
  }, [track, isDataLoading]);

  if (isDataLoading) {
    return <PageLayout><Spinner /></PageLayout>;
  }

  return (
    <Matryoshka wrappers={CHECKOUT_PROVIDERS}>
      {isDesktop ? <Tablet /> : <Mobile />}
      <Blocker />
    </Matryoshka>
  );
});

